import { FigmaAutoLayout } from './FigmaLayout';
import { clampVwMax, pxArt, pxH, pxW } from './figmaScale';
import { FIGMA_SPEC } from './figmaSpec';
import styles from './GoldFigmaComponents.module.scss';
import rootStyles from './GoldPageRoot.module.scss';
import bgStyles from './goldBackgrounds.module.scss';

const BG_CLASS = {
  entry: bgStyles.bgEntry,
  camera: bgStyles.bgCamera,
  final: bgStyles.bgFinal,
};

export function GoldPageRoot({ children, className = '', bg = 'entry' }) {
  return (
    <div className={`${rootStyles.root} ${BG_CLASS[bg] || BG_CLASS.entry} ${className}`.trim()}>
      <div className={rootStyles.inner}>{children}</div>
    </div>
  );
}

/** Group 1244831442 — fixed header slots (capture / countdown / preview) */
export function Group1244831442({ title, hint, phase = 'capture', hideTitle = false }) {
  const f = FIGMA_SPEC.cameraFlow;
  const isPreview = phase === 'preview';

  return (
    <FigmaAutoLayout
      direction="vertical"
      align="center"
      className={styles.group1244831442}
      style={{ maxWidth: pxW(f.headerW), width: '100%', gap: pxArt(f.headerGap) }}
    >
      <div
        className={styles.cameraTitleSlot}
        style={{ minHeight: pxArt(f.titleSlotH) }}
      >
        <h1
          className={`${styles.textCameraFlowTitle} ${
            isPreview ? styles.textCameraFlowTitlePreview : ''
          } ${hideTitle ? styles.cameraTitleVisuallyHidden : ''}`.trim()}
          style={{ fontSize: pxArt(f.titleFontSize) }}
          aria-hidden={hideTitle || undefined}
        >
          {title}
        </h1>
      </div>
      <div
        className={styles.cameraHintSlot}
        style={{ minHeight: pxArt(f.hint.h) }}
      >
        <p
          className={styles.textCameraFlowHint}
          style={{
            fontSize: pxArt(f.hint.fontSize),
            lineHeight: f.hint.lineHeight,
            maxWidth: pxW(f.hint.w),
          }}
        >
          {hint}
        </p>
      </div>
    </FigmaAutoLayout>
  );
}

/** image 292 + Vector 1 + Vector 2 */
export function Image292Frame({ width, height, children, overlay }) {
  const f = FIGMA_SPEC.camera.frame;
  const corner = pxH(FIGMA_SPEC.camera.corner.arm);

  return (
    <div
      className={styles.image292}
      style={{
        width: width ?? clampVwMax(f.w),
        ...(height != null
          ? { height }
          : { aspectRatio: `${f.w} / ${f.h}` }),
      }}
    >
      <span
        className={styles.vector1}
        style={{ width: corner, height: corner }}
      />
      <span
        className={styles.vector2}
        style={{ width: corner, height: corner }}
      />
      <div className={styles.webcamLayer}>{children}</div>
      {overlay}
    </div>
  );
}

/** Preview: Retake | Continue row below frame (Figma 3455:436) */
export function PreviewFrameControls({ onRetake, onContinue, arrowSrc }) {
  const p = FIGMA_SPEC.preview;
  const barH = pxH(p.retakeBar.h);
  const btnFont = pxH(p.btnText.fontSize);

  return (
    <div
      className={`${styles.previewActionsRow} ${styles.previewActionsDock}`}
      style={{ gap: pxW(p.actionsGap) }}
    >
      <button
        type="button"
        className={`${styles.frame427319938} ${styles.previewBar}`}
        style={{ height: barH, fontSize: btnFont }}
        onClick={onRetake}
      >
        Retake
      </button>
      <button
        type="button"
        className={`${styles.frame427319937} ${styles.previewBar} ${styles.previewBarContinue}`}
        style={{ height: barH, fontSize: btnFont }}
        onClick={onContinue}
      >
        <span>Continue</span>
        <span
          className={`${styles.arrowRightAlt} ${styles.previewBarArrow}`}
        >
          <img src={arrowSrc} alt="" />
        </span>
      </button>
    </div>
  );
}

export function CameraButton({ onClick, src, alt = '' }) {
  return (
    <button type="button" className={styles.cameraDockBtn} aria-label="Capture" onClick={onClick}>
      <img src={src} alt={alt} />
    </button>
  );
}

/** Countdown 3-2-1 — fills same dock slot as capture button */
export function CountdownDock({ src, alt = '' }) {
  return (
    <div className={styles.cameraDockCountdown} aria-hidden>
      <img src={src} alt={alt} />
    </div>
  );
}

export function Frame427319938Retake({ onClick, label, barStyle, fontSize }) {
  return (
    <button
      type="button"
      className={`${styles.frame427319938} ${styles.frame427319938Top}`}
      style={barStyle}
      onClick={onClick}
    >
      <span className={styles.frame427319938Inner} style={{ fontSize }}>
        {label}
      </span>
    </button>
  );
}

export function Frame427319937Continue({ onClick, label, barStyle, fontSize, arrowSrc }) {
  return (
    <button
      type="button"
      className={`${styles.frame427319937} ${styles.frame427319937Bottom}`}
      style={barStyle}
      onClick={onContinue}
    >
      <span className={styles.frame427319938Inner} style={{ fontSize }}>
        {label}
        <span
          className={styles.arrowRightAlt}
          style={{
            width: pxH(FIGMA_SPEC.preview.btnArrow.w),
            height: pxH(FIGMA_SPEC.preview.btnArrow.h),
            marginLeft: pxW(8),
          }}
        >
          <img src={arrowSrc} alt="" style={{ width: pxW(22), height: pxH(22) }} />
        </span>
      </span>
    </button>
  );
}

export function Frame427319936Start({
  onClick,
  label,
  outerStyle,
  innerStyle,
  labelStyle,
  arrowStyle,
  iconStyle,
  arrowSrc,
  className = '',
}) {
  return (
    <button
      type="button"
      className={`${styles.frame427319936} ${className}`.trim()}
      style={outerStyle}
      onClick={onClick}
    >
      <span className={styles.frame427319938Cta} style={innerStyle}>
        <span className={styles.startExperience} style={labelStyle}>
          {label.toUpperCase()}
        </span>
        <span
          className={`${styles.arrowRightAlt} ${styles.arrowRightAltEntry}`}
          style={arrowStyle}
        >
          <img src={arrowSrc} alt="" style={iconStyle} />
        </span>
      </span>
    </button>
  );
}

export function Group1244831445({ percent, ringStyle }) {
  const p = FIGMA_SPEC.processing;
  const pct = Math.min(99, Math.round(percent));
  const circumference = 2 * Math.PI * 52;
  return (
    <div className={styles.group1244831445} style={ringStyle}>
      <svg viewBox="0 0 120 120" className={styles.ringSvg} aria-hidden>
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="6" />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="#d4af37"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <span className={styles.percent76} style={{ fontSize: pxH(p.percent.fontSize) }}>
        {pct}%
      </span>
    </div>
  );
}

export function Group1244831449({ sparkleSrc, text, animateIcon = false }) {
  const p = FIGMA_SPEC.processing;
  return (
    <FigmaAutoLayout
      direction="horizontal"
      align="center"
      justify="center"
      gap={text ? 20 : 0}
      className={styles.group1244831449}
    >
      <img
        src={sparkleSrc}
        alt=""
        className={`${styles.sparkles} ${animateIcon ? styles.sparklesAnimated : ''}`.trim()}
        style={{ width: pxH(p.sparkles.w), height: pxH(p.sparkles.h) }}
      />
      {text ? (
        <p
          className={styles.statusCopy}
          style={{
            fontSize: clampVwMax(p.statusText.fontSize),
            lineHeight: p.statusText.lineHeight,
            maxWidth: pxW(p.statusText.w),
          }}
        >
          {text}
        </p>
      ) : null}
    </FigmaAutoLayout>
  );
}
